import React, { useEffect, useState } from "react";
import { ItemModel } from "../models/ItemModelTypes";
import {
  createItemModel,
  deleteItemModel,
  fetchItemModelData,
  updateItemModel,
} from "../api/ItemModelService";
import TableComponent from "../pages/TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import { Plus, X } from "lucide-react";
import Select from "react-select";
import CustomAlert from "../view/common/alert/CustomAlert";

import "../styles/Pagination.css";
import "../styles/Button.css";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/icon-style.css";
import "../styles/ItemModels.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/custom-alert.css";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";
import { customStyles } from "../styles/common/customFormStyles";

const ItemModels: React.FC = () => {
  const [itemModels, setItemModels] = useState<ItemModel[]>([]);
  const [currentModel, setCurrentModel] = useState<ItemModel>({
    code: "",
    name: "",
    status: "ACTIVE",
  });
  const [editMode, setEditMode] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [menuTitle, setMenuTitle] = useState<string>("Item Models");
  const [validationErrors, setValidationErrors] = useState<{
    code?: string;
    name?: string;
    status?: string;
  }>({});

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => {}
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => {}
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);
  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const permission = useSelector((state: RootState) => state.permissions)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initializeItemModel, isItemModelInitialized] = useState(false);

  useEffect(() => {
    const breadcrumbElements = document.querySelectorAll('.breadcrumb-nav span');
    if (breadcrumbElements.length >= 2) {
      const menuTitleElement = breadcrumbElements[breadcrumbElements.length - 1];
      setMenuTitle(menuTitleElement.textContent || "Item Models");
    }
  }, []);
  useEffect(() => {
    dispatch(resetFilters());
    isItemModelInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (initializeItemModel) {
      loadData();
    }
  }, [
    initializeItemModel,
    currentPage,
    itemsPerPage,
    searchTerm,
    filters,
    sortBy,
    sortOrder,
  ]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const result = await fetchItemModelData({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        filter: filters,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (result.data) {
        setItemModels(result.data.items || []);
        setTotalItems(result.data.total ?? 0);
        setTotalPages(
          result.data.totalPages ??
            Math.ceil((result.data.total ?? 0) / itemsPerPage)
        );
      }
    } catch (err) {
      setError("Failed to load item models");
    }
    setIsLoading(false);
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => {},
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const checkForDuplicates = (): boolean => {
    const existingModels = editMode
      ? itemModels.filter((model) => model._id !== currentModel._id)
      : itemModels;

    const duplicateCode = existingModels.find(
      (model) => model.code.toLowerCase() === currentModel.code.toLowerCase()
    );

    const duplicateName = existingModels.find(
      (model) => model.name.toLowerCase() === currentModel.name.toLowerCase()
    );

    const errors: { code?: string; name?: string } = { ...validationErrors };

    if (duplicateCode) {
      errors.code = "Code already exists";
    }

    if (duplicateName) {
      errors.name = "Name already exists";
    }

    setValidationErrors(errors);

    if (duplicateCode || duplicateName) {
      configureAlert(
        "Duplicate entry!",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      return true;
    }

    return false;
  };

  const validateForm = (): boolean => {
    const errors: { code?: string; name?: string; status?: string } = {};

    if (!currentModel.code.trim()) {
      errors.code = "Code is required";
    }

    if (!currentModel.name.trim()) {
      errors.name = "Name is required";
    }

    if (editMode && !currentModel.status) {
      errors.status = "Status is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  const handleAddModel = () => {
    setCurrentModel({ code: "", name: "", status: "ACTIVE" });
    setValidationErrors({});
    setEditMode(false);
    setIsModalOpen(true);
  };

  const handleEdit = (model: ItemModel) => {
    setCurrentModel(model);
    setValidationErrors({});
    setEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const modelToDelete = itemModels.find((model) => model._id === id);

    if (modelToDelete) {
      configureAlert(
        `Are you sure you want to Inactive ${modelToDelete.name}?`,
        async () => {
          closeAlert();

          try {
            await deleteItemModel(id);
            await loadData();

            configureAlert(
              `Model has been Inactive successfully.`,
              () => {
                closeAlert();
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          } catch (error) {
            console.error("Error deleting model:", error);
            configureAlert(
              "Failed to delete model.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    }
  };
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
  
    if (isSubmitting) {
      return;
    }
  
    if (!validateForm()) {
      return;
    }
  
    if (checkForDuplicates()) {
      return;
    }
    setIsSubmitting(true);
    setIsLoading(true);
    
    try {
      let updatedModel = { ...currentModel };
  
      if (editMode) {
        const originalModel = itemModels.find(
          (model) => model._id === currentModel._id
        );
  
        const hasChanges =
          !originalModel ||
          originalModel.code !== currentModel.code ||
          originalModel.name !== currentModel.name ||
          originalModel.status !== currentModel.status;
  
        if (!hasChanges) {
          setIsModalOpen(false);
  
          configureAlert(
            "No changes were made.",
            closeAlert,
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
          setIsLoading(false);
          setIsSubmitting(false);
          return;
        }
  
        if (currentModel?.status === "INACTIVE") {
          updatedModel.status = "ACTIVE";
        }
  
        await updateItemModel(currentModel._id as string, updatedModel);
        setIsModalOpen(false);
  
        configureAlert(
          `${currentModel.name} has been updated successfully.`,
          () => {
            loadData();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      } else {
        await createItemModel(updatedModel);
        setIsModalOpen(false);
  
        configureAlert(
          `${currentModel.name} has been added successfully.`,
          () => {
            loadData();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      }
    } catch (error) {
      console.error("Error saving model:", error);
      configureAlert(
        `Failed to ${editMode ? "update" : "add"} model.`,
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    } finally {
      setIsLoading(false);
      setIsSubmitting(false); 
    }
  };
  const handleModalClose = () => {
    if (
      isModalOpen &&
      (editMode ||
        currentModel.code.trim() !== "" ||
        currentModel.name.trim() !== "")
    ) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsModalOpen(false);
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    } else {
      setIsModalOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentModel((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSelectChange = (selectedOption: any, { name }: any) => {
    setCurrentModel((prev) => ({
      ...prev,
      [name]: selectedOption?.value || "",
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const columns = [
    { key: "code", label: "Code", sortable: true },
    { key: "name", label: "Name", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  const statusOptions = [
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" },
  ];

  return (
    <div className="item-models-container">
      <style>{customStyles}</style>

      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Master Item</span>
              <span>Item Models</span>
            </div>
            {permission.menus.itemmodels.create ?
            <div className="items-button-container tooltip-wrapper">
              <button className="item-models-add-btn" onClick={handleAddModel}>
                <Plus size={16} />
              </button>
            </div>
            :<></>}
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {isLoading ? (
        <div className="spinner"></div>
      ) : (
        <div className="box-container">
          <TableComponent
            columns={columns}
            data={itemModels}
            onEdit={handleEdit}
            onDelete={handleDelete}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            menuTitle={menuTitle}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalItems={totalItems}
          />
        </div>
      )}

      {isModalOpen && (
        <div className="item-models-modal-overlay">
          <div className="item-models-modal">
            <div className="item-models-modal-header">
              <h5>{editMode ? "Edit Model" : "Add New Model"}</h5>
              <button
                className="item-models-modal-close"
                onClick={handleModalClose}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSave} noValidate>
              <div className="form-group">
                <input
                  type="text"
                  name="code"
                  placeholder="Code"
                  value={currentModel.code}
                  onChange={handleInputChange}
                  className={validationErrors.code ? "input-error" : ""}
                  required
                />
                {validationErrors.code && (
                  <div className="form-error-message">
                    {validationErrors.code}
                  </div>
                )}
              </div>

              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={currentModel.name}
                  onChange={handleInputChange}
                  className={validationErrors.name ? "input-error" : ""}
                  required
                />
                {validationErrors.name && (
                  <div className="form-error-message">
                    {validationErrors.name}
                  </div>
                )}
              </div>

              {editMode && (
                <div className="select-container">
                  <Select
                    name="status"
                    options={statusOptions}
                    value={statusOptions.find(
                      (option) => option.value === currentModel.status
                    )}
                    onChange={handleSelectChange}
                    placeholder="Select Status"
                    className={validationErrors.status ? "select-error" : ""}
                    classNamePrefix="react-select"
                  />
                  {validationErrors.status && (
                    <div className="form-error-message">
                      {validationErrors.status}
                    </div>
                  )}
                </div>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className="submit-button"
              >
                {isLoading ? "Saving..." : editMode ? "Update" : "Add"}
              </button>
            </form>
          </div>
        </div>
      )}

      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default ItemModels;
